import { useState, useEffect } from 'react';
import { getCitationUrl } from '@/utils/jsonTyping';
import { ChevronDown, ChevronUp, Lightbulb, ChevronLeft, ChevronRight, ExternalLink, ShieldCheck, BookOpen } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { Button } from '@/components/ui/button';
import { Json } from '@/integrations/supabase/types';

interface InsightItem {
  text: string;
  context?: string;
  source?: {
    name: string;
    url?: string;
  };
  priority?: string;
}

interface InsightCategory {
  id: string;
  title: string;
  icon: React.ReactNode;
  bgColor: string;
  items: InsightItem[];
}

interface EditorInsightsPanelProps {
  perplexityInsights: Json | null;
  altHooks: Json | null;
  textOverlays: Json | null;
  improvementsApplied: Json | null;
}

const STORAGE_KEY = 'edytor-insights-expanded';

const parsePerplexityInsights = (data: Json | null): InsightItem[] => {
  if (!data) return [];

  // Handle the nested structure: { insights: [...], citations: [...], metadata: {...} }
  if (typeof data === 'object' && data !== null && !Array.isArray(data)) {
    const obj = data as Record<string, unknown>;
    const citations = Array.isArray(obj.citations) ? obj.citations as string[] : [];

    if (Array.isArray(obj.insights)) {
      return (obj.insights as Array<Record<string, unknown>>).map(item => {
        const text = item.fact?.toString() || item.content?.toString() || item.text?.toString() || '';
        const context = item.context?.toString() || '';

        // New structure: use final_source_url and final_source_name directly
        const finalSourceUrl = item.final_source_url?.toString();
        const finalSourceName = item.final_source_name?.toString();
        const priority = item.final_source_priority?.toString();

        // Fallback to old structure for backwards compatibility
        const sourceStr = item.source?.toString() || '';
        const resolvedUrl = finalSourceUrl || getCitationUrl(sourceStr, citations) || undefined;
        const sourceName = finalSourceName || sourceStr.replace(/\s*\[\d+\]\s*$/, '').trim();

        return {
          text,
          context: context || undefined,
          source: sourceName ? {
            name: sourceName,
            url: resolvedUrl,
          } : undefined,
          priority: priority || undefined,
        };
      }).filter(item => item.text);
    }

    if (Array.isArray(obj.facts)) return parsePerplexityInsights(obj.facts as Json);
    if (Array.isArray(obj.items)) return parsePerplexityInsights(obj.items as Json);
    if (obj.content) return [{ text: String(obj.content) }];
  }

  if (Array.isArray(data)) {
    return data.map(item => {
      if (typeof item === 'string') return { text: item };
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        const text = obj.content?.toString() || obj.text?.toString() || obj.fact?.toString() || obj.insight?.toString() || '';
        const sourceStr = obj.source?.toString() || '';
        const sourceName = sourceStr.replace(/\s*\[\d+\]\s*$/, '').trim();

        return {
          text,
          source: sourceName ? { name: sourceName } : undefined,
        };
      }
      return { text: String(item) };
    }).filter(item => item.text);
  }

  if (typeof data === 'string') {
    return [{ text: data }];
  }

  return [];
};

const getPriorityBadge = (priority?: string) => {
  if (!priority) return null;
  const p = priority.toUpperCase();
  if (p === 'P1' || p === 'P2') {
    return {
      label: 'Znakomite źródło',
      icon: ShieldCheck,
      className: 'bg-emerald-500/15 text-emerald-600 dark:text-emerald-400 border-emerald-500/30',
    };
  }
  // P3/P4 — positive but softer badge
  return {
    label: 'Publikacja',
    icon: BookOpen,
    className: 'bg-sky-500/15 text-sky-600 dark:text-sky-400 border-sky-500/30',
  };
};

const parseJsonToInsightItems = (data: Json | null): InsightItem[] => {
  if (!data) return [];
  
  if (Array.isArray(data)) {
    return data.map(item => {
      if (typeof item === 'string') return { text: item };
      if (typeof item === 'object' && item !== null) {
        const obj = item as Record<string, unknown>;
        return { text: obj.content?.toString() || obj.text?.toString() || obj.value?.toString() || JSON.stringify(item) };
      }
      return { text: String(item) };
    }).filter(item => item.text);
  }
  
  if (typeof data === 'object' && data !== null) {
    const obj = data as Record<string, unknown>;
    if (Array.isArray(obj.items)) return parseJsonToInsightItems(obj.items as Json);
    if (obj.content) return [{ text: String(obj.content) }];
    if (obj.text) return [{ text: String(obj.text) }];
  }
  
  if (typeof data === 'string') {
    return [{ text: data }];
  }
  
  return [];
};

export const EditorInsightsPanel = ({
  perplexityInsights,
  altHooks,
  textOverlays,
}: EditorInsightsPanelProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const [categoryIndexes, setCategoryIndexes] = useState<Record<string, number>>({});
  const [expandedContexts, setExpandedContexts] = useState<Record<string, boolean>>({});

  const toggleContext = (key: string) => {
    setExpandedContexts(prev => ({ ...prev, [key]: !prev[key] }));
  };

  // Load saved expanded state
  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved === 'true') setIsOpen(true);
  }, []);

  // Save expanded state
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, String(isOpen));
  }, [isOpen]);

  // Build insight categories from data (removed "Zastosowane ulepszenia")
  const categories: InsightCategory[] = [];

  const ciekawostki = parsePerplexityInsights(perplexityInsights);
  if (ciekawostki.length > 0) {
    categories.push({
      id: 'ciekawostki',
      title: 'Ciekawostki',
      icon: <span className="text-lg">💡</span>,
      bgColor: 'bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/20',
      items: ciekawostki,
    });
  }

  const hooks = parseJsonToInsightItems(altHooks);
  if (hooks.length > 0) {
    categories.push({
      id: 'hooks',
      title: 'Hook',
      icon: <span className="text-lg">🎣</span>,
      bgColor: 'bg-gradient-to-br from-blue-500/10 to-blue-500/5 border-blue-500/20',
      items: hooks,
    });
  }

  const overlays = parseJsonToInsightItems(textOverlays);
  if (overlays.length > 0) {
    categories.push({
      id: 'overlays',
      title: 'Nakładki tekstowe',
      icon: <span className="text-lg">📝</span>,
      bgColor: 'bg-gradient-to-br from-orange-500/10 to-orange-500/5 border-orange-500/20',
      items: overlays,
    });
  }

  // No insights - don't render anything
  if (categories.length === 0) {
    return null;
  }
  const getCurrentIndex = (categoryId: string) => categoryIndexes[categoryId] || 0;

  const navigateInsight = (categoryId: string, direction: 'prev' | 'next', totalItems: number) => {
    setCategoryIndexes(prev => {
      const current = prev[categoryId] || 0;
      let newIndex = direction === 'next' ? current + 1 : current - 1;
      if (newIndex < 0) newIndex = totalItems - 1;
      if (newIndex >= totalItems) newIndex = 0;
      return { ...prev, [categoryId]: newIndex };
    });
  };

  return (
    <Collapsible open={isOpen} onOpenChange={setIsOpen}>
      <CollapsibleTrigger asChild>
        <button 
          className={`flex items-center gap-2 w-full transition-all duration-200 ${
            isOpen 
              ? 'text-sm text-muted-foreground py-2' 
              : 'bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 rounded-lg px-4 py-3 hover:shadow-sm cursor-pointer'
          }`}
        >
          <Lightbulb className="w-4 h-4 text-amber-500" />
          <span className={isOpen ? 'font-medium text-sm' : 'font-semibold text-[0.9375rem]'}>
            Insights i podpowiedzi
          </span>
          <span className="ml-auto">
            {isOpen ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </span>
        </button>
      </CollapsibleTrigger>

      <CollapsibleContent className="transition-all duration-200">
        <div className="grid gap-3 pt-2 pb-4 grid-cols-1 sm:grid-cols-2 sm:grid-rows-[auto_auto]">
          {categories.map((category) => {
            const currentIndex = getCurrentIndex(category.id);
            const currentItem = category.items[currentIndex];
            const hasMultiple = category.items.length > 1;

            // Determine grid position based on category
            const gridClass = category.id === 'ciekawostki' 
              ? 'sm:col-start-2 sm:row-start-1 sm:row-span-2' 
              : category.id === 'hooks' 
                ? 'sm:col-start-1 sm:row-start-1' 
                : 'sm:col-start-1 sm:row-start-2';

            // Determine button styles based on category
            const buttonStyles = category.id === 'ciekawostki' 
              ? 'bg-emerald-500/10 border-emerald-500/30 hover:bg-emerald-500 hover:text-white' 
              : category.id === 'hooks' 
                ? 'bg-blue-500/10 border-blue-500/30 hover:bg-blue-500 hover:text-white' 
                : 'bg-orange-500/10 border-orange-500/30 hover:bg-orange-500 hover:text-white';

            return (
              <div
                key={category.id}
                className={`rounded-xl p-4 ${category.bgColor} border shadow-sm ${gridClass} flex flex-col`}
              >
                {/* Header */}
                <div className="flex items-center gap-2 mb-3">
                  {category.icon}
                  <h3 className="text-sm font-bold text-foreground">{category.title}</h3>
                </div>

                {/* Content */}
                <div className={`p-4 rounded-lg bg-background/80 border ${
                  category.id === 'ciekawostki'
                    ? 'border-emerald-500/10'
                    : category.id === 'hooks'
                      ? 'border-blue-500/10'
                      : 'border-orange-500/10'
                } mb-3 flex-1`}>
                  {/* Priority badge for Ciekawostki */}
                  {category.id === 'ciekawostki' && (() => {
                    const badge = getPriorityBadge(currentItem.priority);
                    if (!badge) return null;
                    const BadgeIcon = badge.icon;
                    return (
                      <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-semibold border mb-3 ${badge.className}`}>
                        <BadgeIcon className="w-3.5 h-3.5" />
                        {badge.label}
                      </span>
                    );
                  })()}

                  <p className={`text-sm leading-relaxed ${category.id === 'hooks' ? 'italic' : ''} ${category.id === 'ciekawostki' ? '' : 'line-clamp-4'}`}>
                    {currentItem.text}
                  </p>

                  {/* Expandable context for Ciekawostki */}
                  {category.id === 'ciekawostki' && currentItem.context && (
                    <div className="mt-3">
                      <button
                        onClick={() => toggleContext(`${category.id}-${currentIndex}`)}
                        className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {expandedContexts[`${category.id}-${currentIndex}`] ? (
                          <>
                            <ChevronUp className="w-3.5 h-3.5" />
                            Ukryj kontekst
                          </>
                        ) : (
                          <>
                            <ChevronDown className="w-3.5 h-3.5" />
                            Dlaczego to ważne?
                          </>
                        )}
                      </button>
                      {expandedContexts[`${category.id}-${currentIndex}`] && (
                        <p className="mt-2 text-xs text-muted-foreground leading-relaxed pl-3 border-l-2 border-emerald-500/20">
                          {currentItem.context}
                        </p>
                      )}
                    </div>
                  )}

                  {/* Source link for Ciekawostki */}
                  {category.id === 'ciekawostki' && currentItem.source && (
                    <a
                      href={currentItem.source.url || '#'}
                      target="_blank"
                      rel="noopener noreferrer"
                      title={currentItem.source.name}
                      className={`
                        inline-flex items-center gap-2 mt-3 px-3 py-2
                        bg-emerald-500/10 dark:bg-emerald-400/10
                        border border-emerald-500/20 dark:border-emerald-400/20
                        rounded-md text-[0.8125rem] text-emerald-600 dark:text-emerald-400
                        transition-all duration-150
                        ${currentItem.source.url ? 'hover:bg-emerald-500/15 hover:scale-[1.02] cursor-pointer' : 'cursor-default'}
                        max-w-full
                      `}
                      onClick={(e) => {
                        if (!currentItem.source?.url) e.preventDefault();
                      }}
                    >
                      <ExternalLink className="w-3.5 h-3.5 shrink-0" />
                      <span className="truncate">{currentItem.source.name}</span>
                    </a>
                  )}
                </div>

                {/* Footer with navigation */}
                {hasMultiple && (
                  <div className="flex items-center justify-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-8 w-8 rounded-full ${buttonStyles} disabled:opacity-30 p-0`}
                      onClick={() => navigateInsight(category.id, 'prev', category.items.length)}
                      disabled={currentIndex === 0}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>
                    <span className="text-sm text-foreground font-medium min-w-[3rem] text-center">
                      {currentIndex + 1}/{category.items.length}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      className={`h-8 w-8 rounded-full ${buttonStyles} disabled:opacity-30 p-0`}
                      onClick={() => navigateInsight(category.id, 'next', category.items.length)}
                      disabled={currentIndex === category.items.length - 1}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CollapsibleContent>
    </Collapsible>
  );
};